import { Component, EventEmitter, forwardRef, Host, Input, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { EoBaseFormControlComponent } from '../eo-base-form-control/eo-base-form-control.component';
import { IEoMultiselectConfig } from './eo-multiselect.model';
import { DropdownChangeEvent, DropdownFilterEvent } from 'primeng/dropdown';
import { EoMultiselectService } from './eo-multiselect.service';

@Component({
  selector: 'eo-multiselect',
  templateUrl: './eo-multiselect.component.html',
  styleUrls: ['./eo-multiselect.component.scss'],
  providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => EoMultiselectComponent),
        multi: true,
      },
      EoMultiselectService,
    ],
})
export class EoMultiselectComponent<T> extends EoBaseFormControlComponent {
  protected override _config?: IEoMultiselectConfig<T>;

  override get config() {
    return this._config;
  }
  override set config(val: IEoMultiselectConfig<T> | undefined) {
    this._config = val;
  }

  httpData$ = this.dataService?.data$;
  httpStatus$ = this.dataService?.status$;
  httpError$ = this.dataService?.error$;

  @Input() dropdownIcon?: string;
  @Input() inputStyleClass?: string;
  @Input() optionLabel?: string;
  @Input() optionValue?: string;
  @Input() options?: T[];
  @Input() dropdownStyleClass?: string;
  @Input() placeholder?: string;
  @Input() loadingPlaceholder?: string;
  @Input() showClear: boolean | null = null;
  @Input() filter = false;
  @Input() group = false;
  @Input() filterBy?: string;
  @Input() filterFields?: any[];
  @Input() filterPlaceholder?: string;
  @Input() appendTo?: any;

  @Input() virtualScrollSize?: number;
  @Input() virtualScroll: boolean = false;
  @Input() override loading = false;
  @Input() apiUrl?: string;

  @Output() onFilter: EventEmitter<DropdownFilterEvent> = new EventEmitter();
  @Output() override onChange: EventEmitter<DropdownChangeEvent> =
    new EventEmitter();

  constructor(@Host() private readonly dataService?: EoMultiselectService<T>) {
    super();
  }

  fetchData(): void {
    const apiUrl = this.getApiUrl();
    if (!apiUrl) return;

    this.dataService?.getData(apiUrl).subscribe();
  }

  override ngOnInit(): void {
    this.fetchData();
  }

  protected override onChangeHandler(event: DropdownChangeEvent) {
    super.onChangeHandler(event);
  }

  protected override onBlurHandler(event: any) {
    super.onBlurHandler(event);
  }

  protected override onFocusHandler(event: any) {
    super.onFocusHandler(event);
  }

  protected onFilterHandler(event: DropdownFilterEvent) {
    this.onFilter.emit(event);
  }

  protected hasInputIcon(): boolean {
    return this.dropdownIcon != null || this.config?.inputIcon != null;
  }

  protected getDropdownIcon(): string {
    if (this.loading) return 'pi pi-spinner pi-spin';
    return this.dropdownIcon ?? this.config?.inputIcon ?? 'pi pi-chevron-down';
  }

  protected getPlaceholder(): string {
    if (this.loading)
      return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';

    return this.placeholder ?? this.config?.placeholder ?? '';
  }

  protected getInputStyleClass(): string {
    return this.inputStyleClass ?? this.config?.inputStyleClass ?? '';
  }

  protected getOptionLabel(): string {
    return this.optionLabel ?? this.config?.optionLabel ?? '';
  }

  protected getOptionValue(): string {
    return this.optionValue ?? this.config?.optionValue ?? '';
  }

  protected getOptions(): T[] {
    return (
      this.options ?? this.config?.options ?? this.httpData$?.getValue() ?? []
    );
  }

  protected getDropdownStyleClass(): string {
    return this.dropdownStyleClass ?? this.config?.dropdownStyleClass ?? '';
  }

  protected getLoadingPlaceholder(): string {
    return this.loadingPlaceholder ?? this.config?.loadingPlaceholder ?? '';
  }

  protected getLoading(): boolean {
    return this.loading || this.httpStatus$?.getValue() === 'loading';
  }

  protected getShowClear(): boolean {
    return this.showClear ?? this.config?.showClear ?? false;
  }

  protected hasFilter(): boolean {
    return this.filter ?? this.config?.filter ?? false;
  }

  protected getFilterBy(): string {
    return this.filterBy ?? this.config?.filterBy ?? '';
  }

  protected getFilterFields(): any[] {
    return this.filterFields ?? this.config?.filterFields ?? [];
  }

  protected getFilterPlaceholder(): string {
    return this.filterPlaceholder ?? this.config?.filterPlaceholder ?? '';
  }

  protected getAppendTo(): any {
    return this.appendTo ?? this.config?.appendTo ?? '';
  }

  protected getGroup(): boolean {
    return this.group ?? this.config?.group ?? false;
  }

  protected getVirtualScrollSize(): number {
    return this.virtualScrollSize ?? this.config?.virtualScrollSize ?? 0;
  }

  protected getVirtualScroll(): boolean {
    return this.virtualScroll ?? this.config?.virtualScroll ?? false;
  }

  protected getApiUrl(): string | undefined {
    return this.apiUrl ?? this.config?.apiUrl;
  }

  protected getMaxSelectedLabels(): number {
    return this.config?.maxSelectedLabels ?? 0;
  }

  protected getDisplay(): string {
    return this.config?.display ?? 'comma';
  }
}
